import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { EnvelopeIcon, SpinnerIcon, WarningIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  EmailRecipientsList,
  type EmailRecipientContact,
} from '@/components/common/EmailRecipientsList';
import { useToast } from 'src/context/ToastContext';
import {
  useAddApRemittanceContact,
  useSupplierContacts,
  useToggleApRemittance,
} from '@/api/rpc/hooks/accounts-payable/useSupplierContacts';
import { useRemittanceEmailPreview } from '@/api/rpc/hooks/finance/useRemittanceEmailPreview';
import { useSendRemittanceEmail } from '@/api/rpc/hooks/finance/useSendRemittanceEmail';

const SUPPLIER_BADGE_CLASS =
  'rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700';

interface SendRemittanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentId: string;
  supplierId: string | null;
  supplierName: string | null;
  isPaid: boolean;
}

export function SendRemittanceDialog({
  open,
  onOpenChange,
  paymentId,
  supplierId,
  supplierName,
  isPaid,
}: SendRemittanceDialogProps) {
  const { showToast } = useToast();
  const { contacts, isLoading: contactsLoading } =
    useSupplierContacts(supplierId);
  const toggleApRemittance = useToggleApRemittance(supplierId);
  const addContact = useAddApRemittanceContact(supplierId);
  const { preview, isPreviewing, previewData, previewError, reset } =
    useRemittanceEmailPreview();
  const { send, isSending } = useSendRemittanceEmail();

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Map<string, 'to' | 'cc'>>(
    () => new Map(),
  );

  const fetchPreview = useCallback(() => {
    preview({ paymentId }).catch(() => {
      // error state handled by previewError
    });
  }, [paymentId, preview]);

  const hasFetchedPreview = useRef(false);
  useEffect(() => {
    if (!open) {
      hasFetchedPreview.current = false;
      return;
    }
    if (!hasFetchedPreview.current) {
      hasFetchedPreview.current = true;
      fetchPreview();
    }
  }, [open, fetchPreview]);

  const emailContacts = useMemo(
    () =>
      contacts.flatMap((contact) => {
        const email = contact.email?.trim().toLowerCase();
        return email ? [{ ...contact, email }] : [];
      }),
    [contacts],
  );

  const taggedContacts = useMemo(
    () => emailContacts.filter((c) => c.apRemittance),
    [emailContacts],
  );

  const untaggedContacts = useMemo(
    () => emailContacts.filter((c) => !c.apRemittance),
    [emailContacts],
  );

  const taggedEmails = useMemo(
    () => taggedContacts.map((c) => c.email),
    [taggedContacts],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedRoles((prev) => {
      const next = new Map<string, 'to' | 'cc'>();
      const taggedSet = new Set(taggedEmails);
      for (const [email, role] of prev) {
        if (taggedSet.has(email)) next.set(email, role);
      }
      const hasTo = Array.from(next.values()).some((r) => r === 'to');
      for (const email of taggedEmails) {
        if (!next.has(email)) {
          next.set(email, !hasTo && next.size === 0 ? 'to' : 'cc');
        }
      }
      if (next.size > 0 && !Array.from(next.values()).includes('to')) {
        const firstKey = next.keys().next().value as string;
        next.set(firstKey, 'to');
      }
      return next;
    });
  }, [open, taggedEmails]);

  function handleToggleRole(email: string) {
    setSelectedRoles((prev) => {
      const current = prev.get(email);
      if (!current) return prev;
      if (current === 'to') {
        const toCount = Array.from(prev.values()).filter(
          (r) => r === 'to',
        ).length;
        if (toCount <= 1) return prev;
        const next = new Map(prev);
        next.set(email, 'cc');
        return next;
      }
      const next = new Map(prev);
      next.set(email, 'to');
      return next;
    });
  }

  const recipients: EmailRecipientContact[] = useMemo(
    () =>
      taggedContacts.map((c) => ({
        id: c.id,
        email: c.email,
        name: c.name,
        badges: (
          <>
            {c.source === 'supplier' && (
              <span className={SUPPLIER_BADGE_CLASS}>
                <T>Supplier</T>
              </span>
            )}
            {c.isPrimary && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                <T>Primary</T>
              </span>
            )}
          </>
        ),
      })),
    [taggedContacts],
  );

  const availableContacts: EmailRecipientContact[] = useMemo(
    () =>
      untaggedContacts.map((c) => ({
        id: c.id,
        email: c.email,
        name: c.name,
        badges: c.source === 'supplier' && (
          <span className={SUPPLIER_BADGE_CLASS}>
            <T>Supplier</T>
          </span>
        ),
      })),
    [untaggedContacts],
  );

  const toEmails = useMemo(
    () =>
      Array.from(selectedRoles.entries())
        .filter(([, role]) => role === 'to')
        .map(([email]) => email),
    [selectedRoles],
  );
  const ccEmails = useMemo(
    () =>
      Array.from(selectedRoles.entries())
        .filter(([, role]) => role === 'cc')
        .map(([email]) => email),
    [selectedRoles],
  );
  const hasRecipients = toEmails.length > 0;

  function handleRemove(contact: EmailRecipientContact) {
    toggleApRemittance.mutate({
      contactId: contact.id,
      apRemittance: false,
    });
  }

  function handleAddExisting(contact: EmailRecipientContact) {
    toggleApRemittance.mutate({
      contactId: contact.id,
      apRemittance: true,
    });
  }

  const gt = useGT();

  async function handleAddNew(email: string) {
    if (!supplierId) return;
    try {
      await addContact.mutateAsync({ supplierId, email });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : gt('Failed to add contact');
      showToast({
        title: gt('Add failed'),
        message,
        color: 'error',
        icon: 'warning',
      });
      throw err;
    }
  }

  async function handleConfirmedSend() {
    if (toEmails.length === 0) return;
    try {
      await send({
        paymentId,
        toEmails,
        ccEmails: ccEmails.length > 0 ? ccEmails : undefined,
      });
      setShowConfirm(false);
      handleOpenChange(false);
      const allRecipients = [...toEmails, ...ccEmails];
      showToast({
        title: gt('Remittance sent'),
        message: gt('Remittance emailed to {recipients}', {
          recipients: allRecipients.join(', '),
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Failed to send remittance email');
      showToast({
        title: gt('Send failed'),
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setShowConfirm(false);
      setSelectedRoles(new Map());
      reset();
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            <T>Remittance Email</T>
            {supplierName ? ` — ${supplierName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 flex flex-col gap-4 overflow-auto">
          {/* Recipients */}
          <div className="space-y-2">
            <p className="text-sm font-medium">
              <T>Recipients</T>
            </p>
            <EmailRecipientsList
              recipients={recipients}
              selectedRoles={selectedRoles}
              onToggleRole={handleToggleRole}
              onRemove={handleRemove}
              availableContacts={availableContacts}
              onAddExisting={handleAddExisting}
              onAddNew={handleAddNew}
              isLoading={contactsLoading}
              isAdding={addContact.isPending}
              addButtonLabel={gt('Add recipient')}
              emptyMessage={gt(
                'No remittance recipients yet. Click “Add recipient” to add one.',
              )}
              disabled={!supplierId}
            />

            {hasRecipients && previewData && (
              <div className="space-y-0.5 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium">
                    <T>From:</T>
                  </span>{' '}
                  {previewData.from}
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          {!previewData && isPreviewing ? (
            <div
              className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground"
              style={{ minHeight: 400 }}
            >
              <SpinnerIcon className="h-4 w-4 animate-spin" />
              <T>Loading preview...</T>
            </div>
          ) : !previewData && previewError ? (
            <div
              className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground"
              style={{ minHeight: 400 }}
            >
              <WarningIcon className="h-4 w-4" />
              <T>Failed to load preview</T>
            </div>
          ) : previewData ? (
            <>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium">
                  <T>Subject:</T>
                </span>{' '}
                {previewData.subject}
              </div>
              <div className="min-h-0 flex-1 overflow-auto rounded border bg-white">
                <iframe
                  title={gt('Remittance email preview')}
                  srcDoc={previewData.htmlContent}
                  className="h-full w-full border-0"
                  style={{ minHeight: 400 }}
                  sandbox=""
                />
              </div>
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            <T>Cancel</T>
          </Button>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!hasRecipients || !isPaid}
            title={
              !isPaid ? gt('Payment must be paid before sending') : undefined
            }
          >
            <EnvelopeIcon className="mr-1.5 h-4 w-4" />
            <T>Send Remittance</T>
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T>Confirm Send</T>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <T>Send remittance to</T>{' '}
            <span className="font-medium text-foreground">
              {toEmails.join(', ')}
            </span>
            {ccEmails.length > 0 && (
              <>
                {' '}
                <T>with Cc to</T>{' '}
                <span className="font-medium text-foreground">
                  {ccEmails.join(', ')}
                </span>
              </>
            )}
            ?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={isSending}
            >
              <T>Cancel</T>
            </Button>
            <Button onClick={handleConfirmedSend} disabled={isSending}>
              {isSending ? (
                <>
                  <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                  <T>Sending...</T>
                </>
              ) : (
                <T>Confirm & Send</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
